import { useCallback, useEffect, useRef, useState } from 'react';

import { getWoundAttachmentUrl, getWoundAttachments } from '../../services/api';
import type { WoundAttachmentResponse } from '../../services/api';

export interface WoundImage {
  attachmentId: string;
  fileName: string;
  url: string;
}

/**
 * Loads photos for one wound. Returns `null` when the attachment list request
 * fails so callers can leave that wound unset (still-loading / unknown) instead
 * of writing `[]`, which the table treats as confirmed-empty.
 *
 * The generated client resolves HTTP errors into `{ error }` rather than
 * throwing unless `throwOnError` is set, so both `resp.error` and thrown
 * failures are handled here.
 */
async function loadImagesForWound(
  patientId: string,
  woundId: string
): Promise<WoundImage[] | null> {
  try {
    const resp = await getWoundAttachments({
      path: { patient_id: patientId, wound_id: woundId },
    });
    // Client has no throwOnError; a failed list returns { error } with no data.
    // Bail before `?? []` or a 4xx/5xx looks like a wound with no photos.
    if (resp.error) {
      console.error('Failed to load wound attachments:', resp.error);
      return null;
    }

    const attachments = ((resp.data as WoundAttachmentResponse[]) ?? []).filter(
      a => a.mime_type.startsWith('image/')
    );

    const images = await Promise.all(
      attachments.map(async attachment => {
        try {
          const urlResp = await getWoundAttachmentUrl({
            path: {
              patient_id: patientId,
              wound_id: woundId,
              attachment_id: attachment.id,
            },
          });
          // Same non-throwing client: an HTTP error has no url and never hits
          // the catch below unless we check `error` explicitly.
          if (urlResp.error) {
            console.error('Failed to load wound image preview:', urlResp.error);
            return null;
          }
          const url = (urlResp.data as { url?: string })?.url;
          if (!url) return null;
          return {
            attachmentId: attachment.id,
            fileName: attachment.file_name,
            url,
          };
        } catch (err) {
          console.error('Failed to load wound image preview:', err);
          return null;
        }
      })
    );

    return images.filter((image): image is WoundImage => image !== null);
  } catch (err) {
    console.error('Failed to load wound attachments:', err);
    return null;
  }
}

/**
 * Loads every wound photo the wounds table can show, newest first.
 *
 * The wound list endpoint carries no attachment data, so this fans out one
 * attachment fetch per wound plus one presigned-URL fetch per photo. Nothing
 * caps that fan-out because the row strip shows a `+N` overflow tile that opens
 * a gallery over the full set, and a wound holds a handful of photos in
 * practice. The assessments drawer already resolves every URL the same way for
 * a single wound. If a wound ever carries enough photos for this to hurt, the
 * fix is to return the URL on `WoundAttachmentResponse` rather than to hide
 * photos from the strip.
 *
 * A photo whose URL cannot be issued is dropped and only logged: the table is a
 * glance surface, and the assessments drawer and edit dialog already raise a
 * toast when a clinician goes looking for the images themselves. A failed
 * attachment-list request leaves that wound unset so the row stays blank
 * instead of showing the empty-state dash.
 *
 * `refresh(woundId)` reloads that wound only. The assessments drawer is where
 * photos change, so callers pass the open wound's id on close instead of
 * re-fetching every wound on the chart.
 */
export function useWoundImages(
  patientId: string,
  woundIds: string[]
): {
  imagesByWound: Record<string, WoundImage[]>;
  refresh: (woundId: string) => void;
} {
  const [imagesByWound, setImagesByWound] = useState<
    Record<string, WoundImage[]>
  >({});
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Effects compare deps by identity, and `woundIds` is rebuilt on every render
  // of the caller. Key the fetch on the joined ids so re-rendering with the same
  // wounds doesn't re-request every photo. Sorted because the caller reorders
  // the list as wounds are added and removed, and order changes nothing here:
  // the result is keyed by wound id.
  const woundIdKey = [...woundIds].sort().join(',');

  useEffect(() => {
    const ids = woundIdKey ? woundIdKey.split(',') : [];
    if (ids.length === 0) {
      setImagesByWound({});
      return;
    }

    let isCancelled = false;

    const loadWoundImages = async () => {
      const entries = await Promise.all(
        ids.map(async (woundId): Promise<[string, WoundImage[] | null]> => [
          woundId,
          await loadImagesForWound(patientId, woundId),
        ])
      );

      if (!isCancelled) {
        // Skip failed loads so those wounds stay unset (blank) rather than [].
        setImagesByWound(
          Object.fromEntries(
            entries.filter(
              (entry): entry is [string, WoundImage[]] => entry[1] !== null
            )
          )
        );
      }
    };

    loadWoundImages();

    return () => {
      isCancelled = true;
    };
  }, [patientId, woundIdKey]);

  const refresh = useCallback(
    (woundId: string) => {
      void loadImagesForWound(patientId, woundId).then(images => {
        if (!mountedRef.current || images === null) return;
        setImagesByWound(prev => ({ ...prev, [woundId]: images }));
      });
    },
    [patientId]
  );

  return { imagesByWound, refresh };
}
